/**
 * Classifier Service
 *
 * Calls the Python classifier microservice for email classification
 * Replaces the legacy LLM-only filtering with 3-tier hybrid approach
 */

import { supabase } from './supabaseClient';

const CLASSIFIER_URL = process.env.REACT_APP_CLASSIFIER_URL || 'http://localhost:8080';

/**
 * Classify emails using the Python classifier service
 * @param {Array} emails - Array of email objects with subject, from, body, headers
 * @param {Function} saveBatchCallback - Callback to save subscriptions in batches
 * @param {boolean} useTier3 - Enable Tier-3 LLM classification for uncertain emails
 * @returns {Object} - { accepted: [], rejected: [] }
 */
export async function classifyEmailsWithService(emails, saveBatchCallback, useTier3 = false, abortSignal = null) {

  const accepted = [];
  const rejected = [];
  const batch = [];
  const tierStats = { rules: 0, ml: 0, llm: 0 };
  const classificationStats = { subscription: 0, cancellation: 0, not_subscription: 0 };

  for (let i = 0; i < emails.length; i++) {
    // Check if sync was aborted
    if (abortSignal?.aborted) {
      break;
    }
    const email = emails[i];
    const progress = `[${i + 1}/${emails.length}]`;

    try {

      // Call classifier service
      const startTime = Date.now();
      const headers = {
        'Content-Type': 'application/json',
      };

      // Get current Supabase session token for JWT auth
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.access_token) {
        headers['Authorization'] = `Bearer ${sessionData.session.access_token}`;
      }

      const response = await fetch(`${CLASSIFIER_URL}/classify`, {
        method: 'POST',
        headers: headers,
        signal: abortSignal,
        body: JSON.stringify({
          id: email.id,
          thread_id: email.threadId,
          subject: email.subject || '',
          from: email.from || '',
          to: email.to || '',
          date: email.date || '',
          body: email.body || '',
          snippet: email.snippet || '',
          headers: email.headers || {},
          labels: email.labelIds || [],
          use_tier3: useTier3, // Enable/disable Tier-3 LLM per user setting
        }),
      });

      const latency = Date.now() - startTime;

      if (!response.ok) {
        console.error(`   ❌ Classifier API error: ${response.status} ${response.statusText}`);
        rejected.push(email);
        classificationStats.not_subscription++;
        continue;
      }

      const result = await response.json();

      // Track tier stats
      tierStats[result.tier_used] = (tierStats[result.tier_used] || 0) + 1;

      // Log Tier-3 LLM classifications with details
      if (result.tier_used === 'llm') {
        if (result.evidence?.tier3_model_used) {
        }
        if (result.evidence?.tier3_reasoning) {
        }
        if (result.evidence?.tier3_email_body) {
        }
      }

      // Check if it's a subscription or cancellation (both are subscription-related)
      if (result.is_subscription) {
        const subscription = convertClassifierToSubscriptionFormat(result, email);

        // VALIDATION: Regular subscriptions MUST have a price (cancellations don't need one)
        if (!result.is_cancellation && (!subscription.price || subscription.price === null)) {
          rejected.push(email);
          classificationStats.not_subscription++;
          continue;
        }

        accepted.push(subscription);
        batch.push(subscription);

        // Log the classification
        const type = result.is_cancellation ? '🚫 CANCELLATION' : '✅ SUBSCRIPTION';
        const tier = result.tier_used.toUpperCase().padEnd(5);
        const conf = `${(result.confidence * 100).toFixed(0)}%`.padStart(4);


        if (result.extracted_data) {
        }

        // Track classification type
        if (result.is_cancellation) {
          classificationStats.cancellation++;
        } else {
          classificationStats.subscription++;
        }

        // Save in batches of 10
        if (batch.length >= 10) {
          await saveBatchCallback(batch);
          batch.length = 0; // Clear batch
        }
      } else {
        rejected.push(email);
        classificationStats.not_subscription++;

        const tier = result.tier_used.toUpperCase().padEnd(5);
        const conf = `${(result.confidence * 100).toFixed(0)}%`.padStart(4);

        // Show Tier-3 LLM details for rejected emails too
        if (result.tier_used === 'llm') {
          if (result.evidence?.tier3_model_used) {
          }
          if (result.evidence?.tier3_reasoning) {
          }
          if (result.evidence?.tier3_email_body) {
          }
        } else if (result.classification_reason) {
        }
      }
    } catch (error) {
      console.error(`   ❌ Error classifying email: ${error.message}`);
      console.error(`   Stack: ${error.stack}`);
      rejected.push(email);
      classificationStats.not_subscription++;
    }
  }

  // Save remaining batch
  if (batch.length > 0) {
    await saveBatchCallback(batch);
  }

  // Print summary statistics

  return { accepted, rejected };
}

/**
 * Convert classifier service response to subscription format for database
 */
function convertClassifierToSubscriptionFormat(result, originalEmail) {
  const extracted = result.extracted_data || {};

  return {
    // Core fields
    name: extracted.name || extracted.service || 'Unknown Service',
    service: extracted.service || extracted.name || 'Unknown Service',
    category: extracted.category || 'Other',
    price: extracted.price || null,
    currency: extracted.currency || 'USD',
    billing_cycle: extracted.billing_cycle || 'monthly',
    status: result.is_cancellation ? 'cancelled' : (
      (extracted.status && extracted.status.toLowerCase() === 'trial') ? 'active' : (extracted.status || 'active')
    ),

    // Dates
    subscription_date: extracted.subscription_date || new Date().toISOString().split('T')[0],
    first_billing_date: extracted.first_billing_date || null,
    last_billing_date: extracted.last_billing_date || null,
    next_billing_date: extracted.next_billing_date || null,
    cancellation_date: result.is_cancellation ? (extracted.cancellation_date || new Date().toISOString().split('T')[0]) : null,
    trial_end_date: extracted.trial_end_date || null,

    // Additional fields
    description: extracted.description || null,
    website_url: extracted.website_url || null,
    event_type: result.is_cancellation ? 'cancellation' : (
      (extracted.status && extracted.status.toLowerCase() === 'trial' || result.classification === 'trial') ? 'trial_start' : (result.classification || 'invoice')
    ),
    invoice_number: extracted.invoice_number || null,
    receipt_number: extracted.receipt_number || null,
    plan_name: extracted.plan_name || null,
    payment_last4: extracted.payment_last4 || null,
    is_shared: extracted.is_shared || false,
    shared_with: extracted.shared_with || null,
    tags: extracted.tags || null,
    notes: extracted.notes || result.classification_reason || null,

    // Email tracking
    imported_from_email: originalEmail.from || null,
    email_link: `https://mail.google.com/mail/u/0/#inbox/${originalEmail.id}`,
    import_source: 'gmail',
    original_email_data: {
      id: originalEmail.id,
      thread_id: originalEmail.threadId,
      subject: originalEmail.subject,
      from: originalEmail.from,
      date: originalEmail.date,
      snippet: originalEmail.snippet,
      body: originalEmail.htmlBody || originalEmail.body, // Use HTML body for rendering, fallback to plain text
      confidence: result.confidence,
      tier_used: result.tier_used,
      classification: result.classification,
    },
  };
}

/**
 * PAID TIER: Classify emails using LLM only (skip Rules/ML)
 * Batches emails together and sends to LLM for comprehensive classification
 *
 * @param {Array} emails - Array of email objects
 * @param {Function} saveBatchCallback - Callback to save subscriptions in batches
 * @param {string} model - Model to use (optional, defaults to config.PAID_TIER_MODEL)
 * @returns {Object} - { accepted: [], rejected: [] }
 */
export async function classifyEmailsPaidTier(emails, saveBatchCallback, model = null, abortSignal = null) {

  const BATCH_SIZE = 10;
  const accepted = [];
  const rejected = [];
  const subscriptionBatch = [];
  const classificationStats = { subscription: 0, cancellation: 0, not_subscription: 0 };

  // Process emails in batches of 10
  for (let i = 0; i < emails.length; i += BATCH_SIZE) {
    const batchEmails = emails.slice(i, i + BATCH_SIZE);
    const batchNum = Math.floor(i / BATCH_SIZE) + 1;
    const totalBatches = Math.ceil(emails.length / BATCH_SIZE);


    // Check if sync was aborted
    if (abortSignal?.aborted) {
      break;
    }

    try {
      const startTime = Date.now();
      const headers = {
        'Content-Type': 'application/json',
      };

      // Get current Supabase session token for JWT auth
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.access_token) {
        headers['Authorization'] = `Bearer ${sessionData.session.access_token}`;
      }

      const response = await fetch(`${CLASSIFIER_URL}/classify/batch/paid`, {
        method: 'POST',
        headers: headers,
        signal: abortSignal,
        body: JSON.stringify({
          emails: batchEmails.map(email => ({
            id: email.id,
            thread_id: email.threadId,
            subject: email.subject || '',
            from: email.from || '',
            to: email.to || '',
            date: email.date || '',
            body: email.body || '',
            snippet: email.snippet || '',
            headers: email.headers || {},
            labels: email.labelIds || [],
          })),
          model: model,
        }),
      });

      const latency = Date.now() - startTime;

      if (!response.ok) {
        console.error(`   ❌ Paid tier API error: ${response.status} ${response.statusText}`);
        // Reject all emails in this batch
        batchEmails.forEach(email => {
          rejected.push(email);
          classificationStats.not_subscription++;
        });
        continue;
      }

      const batchResult = await response.json();

      // Process each result
      for (const result of batchResult.results) {
        const originalEmail = batchEmails[result.email_index];

        if (result.is_subscription) {
          const subscription = convertPaidTierToSubscriptionFormat(result, originalEmail);

          // VALIDATION: Regular subscriptions MUST have a price (cancellations don't need one)
          if (!result.is_cancellation && (!subscription.price || subscription.price === null)) {
            rejected.push(originalEmail);
            classificationStats.not_subscription++;
            continue;
          }

          accepted.push(subscription);
          subscriptionBatch.push(subscription);

          const type = result.is_cancellation ? '🚫 CANCEL' : '✅ SUB';

          if (result.is_cancellation) {
            classificationStats.cancellation++;
          } else {
            classificationStats.subscription++;
          }
        } else {
          rejected.push(originalEmail);
          classificationStats.not_subscription++;
        }
      }

      // Save subscriptions in batches of 10
      if (subscriptionBatch.length >= 10) {
        await saveBatchCallback(subscriptionBatch);
        subscriptionBatch.length = 0;
      }
    } catch (error) {
      console.error(`   ❌ Batch error: ${error.message}`);
      batchEmails.forEach(email => {
        rejected.push(email);
        classificationStats.not_subscription++;
      });
    }
  }

  // Save remaining subscriptions
  if (subscriptionBatch.length > 0) {
    await saveBatchCallback(subscriptionBatch);
  }

  // Print summary

  return { accepted, rejected };
}

/**
 * Convert paid tier result to subscription format for database
 */
function convertPaidTierToSubscriptionFormat(result, originalEmail) {
  const extracted = result.extracted_data || {};

  return {
    // Core fields
    name: extracted.name || extracted.service || 'Unknown Service',
    service: extracted.service || extracted.name || 'Unknown Service',
    category: extracted.category || 'Other',
    price: extracted.price || null,
    currency: extracted.currency || 'USD',
    billing_cycle: extracted.billing_cycle || 'monthly',
    status: result.is_cancellation ? 'cancelled' : (
      (extracted.status && extracted.status.toLowerCase() === 'trial') ? 'active' : (extracted.status || 'active')
    ),

    // Dates
    subscription_date: extracted.subscription_date || new Date().toISOString().split('T')[0],
    first_billing_date: extracted.first_billing_date || null,
    last_billing_date: extracted.last_billing_date || null,
    next_billing_date: extracted.next_billing_date || null,
    cancellation_date: result.is_cancellation ? (extracted.cancellation_date || new Date().toISOString().split('T')[0]) : null,
    trial_end_date: extracted.trial_end_date || null,

    // Additional fields
    description: extracted.description || null,
    website_url: extracted.website_url || null,
    event_type: result.is_cancellation ? 'cancellation' : (
      (extracted.status && extracted.status.toLowerCase() === 'trial' || extracted.event_type === 'trial') ? 'trial_start' : (extracted.event_type || 'invoice')
    ),
    invoice_number: extracted.invoice_number || null,
    receipt_number: extracted.receipt_number || null,
    plan_name: extracted.plan_name || null,
    payment_last4: extracted.payment_last4 || null,
    is_shared: extracted.is_shared || false,
    shared_with: extracted.shared_with || null,
    tags: extracted.tags || null,
    notes: extracted.notes || result.reasoning || null,

    // Email tracking
    imported_from_email: originalEmail.from || null,
    email_link: `https://mail.google.com/mail/u/0/#inbox/${originalEmail.id}`,
    import_source: 'gmail',
    original_email_data: {
      id: originalEmail.id,
      thread_id: originalEmail.threadId,
      subject: originalEmail.subject,
      from: originalEmail.from,
      date: originalEmail.date,
      snippet: originalEmail.snippet,
      body: originalEmail.htmlBody || originalEmail.body,
      confidence: result.confidence,
      tier_used: 'llm_paid',
      classification: result.is_subscription ? 'subscription' : 'not_subscription',
    },
  };
}

/**
 * Get available paid tier models from the classifier service
 */
export async function getPaidTierModels() {
  try {
    const response = await fetch(`${CLASSIFIER_URL}/models/paid`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error('Failed to get paid tier models:', error.message);
    return null;
  }
}

/**
 * Check if classifier service is available
 */
export async function checkClassifierHealth() {
  try {
    const response = await fetch(`${CLASSIFIER_URL}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      const health = await response.json();
      return {
        available: true,
        version: health.version,
        adjudicator_enabled: health.adjudicator_enabled,
        ml_model_loaded: health.ml_model_loaded,
      };
    }

    return { available: false };
  } catch (error) {
    console.error('Classifier service not available:', error.message);
    return { available: false };
  }
}
